import { shallowRef, readonly, watch, type Ref } from 'vue';

/**
 * Module-level state.
 *
 * captchaToken  – the raw token string passed to onSuccess. This is the
 *                 most reliable source: no async, no timeout risk.
 * fetchPatched  – once-guard so we only wrap window.fetch once per page load.
 * captchaRequiredCallback – registered by useWafCaptcha when mounted. Called by
 *                 patchFetch when WAF demands a CAPTCHA solve (405 response).
 *                 Returns a promise that resolves with a fresh token after the
 *                 user completes the CAPTCHA.
 *
 * WHY NOT AwsWafIntegration.fetch as window.fetch replacement:
 *   AwsWafIntegration.fetch reads window.fetch lazily (at call time).
 *   window.fetch = AwsWafIntegration.fetch creates infinite recursion → crash.
 *
 * CORRECT APPROACH:
 *   Save native fetch as originalFetch, then wrap window.fetch.
 *   In the wrapper, read the stored CAPTCHA token (or fall back to getToken())
 *   and inject it as x-aws-waf-token for cross-domain Cognito requests.
 *   Amplify's fetchTransferHandler calls the global `fetch` at call time,
 *   so this wrapper is guaranteed to intercept every Amplify → Cognito request.
 *
 * TOKEN DOMAIN:
 *   The WAF web ACL on Cognito must have the page hostname in its token domain
 *   list for WAF to accept tokens issued by the CAPTCHA SDK on this page.
 *
 * captchaToken persists at module level across component re-mounts (e.g. locale
 *   switches that force :key re-creation). New instances initialise `solved` from
 *   captchaToken !== null so waitForCaptcha() skips the overlay when a token is
 *   already available.
 */
let captchaToken: string | null = null;
let fetchPatched = false;
let captchaRequiredCallback: (() => Promise<string>) | null = null;
const captchaSolvedRef = shallowRef(false);

/**
 * Registered by useWafCaptcha when it mounts. Allows patchFetch to trigger
 * the CAPTCHA overlay when WAF demands a solve.
 */
export function registerCaptchaRequiredCallback(
  cb: () => Promise<string>,
): void {
  captchaRequiredCallback = cb;
}

export function unregisterCaptchaRequiredCallback(): void {
  captchaRequiredCallback = null;
}

/**
 * Read the aws-waf-token cookie value set by the WAF SDK.
 * Returns null if the cookie is absent.
 */
function getCookieToken(): string | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith('aws-waf-token='));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

export function getCaptchaToken(): string | null {
  return captchaToken;
}

export function patchFetch(): void {
  if (fetchPatched) return;
  fetchPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    let url: string;
    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.href;
    } else {
      url = (input as Request).url;
    }

    if (!url.includes('cognito-idp')) {
      return originalFetch(input, init);
    }

    // Token resolution order (most reliable → least):
    // 1. Cookie: aws-waf-token — always up-to-date, set by the WAF SDK on every
    //    page load. Works on page refresh even before the CAPTCHA widget renders.
    // 2. captchaToken: stored in-memory from the CAPTCHA onSuccess callback.
    // 3. getToken(): async SDK call with a 5-second timeout as last resort.
    let token: string | null = getCookieToken() ?? captchaToken;

    if (!token && window.AwsWafIntegration) {
      try {
        token = await Promise.race([
          window.AwsWafIntegration.getToken(),
          new Promise<null>((resolve) => {
            setTimeout(() => resolve(null), 5000);
          }),
        ]);
      } catch (e) {
        // getToken() timed out or failed — proceed without token
      }
    }

    let requestInit = init;
    if (token) {
      const headers = new Headers(init?.headers);
      headers.set('x-aws-waf-token', token);
      requestInit = { ...init, headers };
    }

    const response = await originalFetch(input, requestInit);

    // WAF returns 405 when it requires a fresh CAPTCHA solve, regardless of
    // whether a token was present. Show the overlay, wait for the user to solve
    // it, then retry with the fresh token.
    if (response.status === 405) {
      captchaToken = null;
      captchaSolvedRef.value = false;
      if (captchaRequiredCallback) {
        try {
          const freshToken = await captchaRequiredCallback();
          captchaToken = freshToken;
          const retryHeaders = new Headers(init?.headers);
          retryHeaders.set('x-aws-waf-token', freshToken);
          return originalFetch(input, { ...init, headers: retryHeaders });
        } catch (e) {
          // CAPTCHA solve failed or was dismissed — return the 405 as-is
        }
      }
    }

    return response;
  };
}

export default function useWafCaptcha(
  container: Ref<HTMLElement | null>,
  locale?: Ref<string>,
) {
  // Patch fetch immediately so the 405 handler is active before any auth request is made.
  // The fetchPatched guard ensures this only runs once per page load.
  patchFetch();

  // Initialise from captchaToken so re-mounts (e.g. locale switch) don't re-show the overlay.
  if (captchaToken !== null) captchaSolvedRef.value = true;
  const solved = captchaSolvedRef;
  const failed = shallowRef(false);
  const loading = shallowRef(true);

  function renderCaptcha(): void {
    if (!container.value) return;
    loading.value = false;

    window.AwsWafCaptcha.renderCaptcha(container.value, {
      apiKey: import.meta.env.VUE_APP_WAF_CAPTCHA_API_KEY,
      defaultLocale: locale?.value,
      onSuccess: (wafToken: string) => {
        captchaToken = wafToken;
        patchFetch();
        captchaSolvedRef.value = true;
      },
      onError: () => {
        failed.value = true;
      },
    });
  }

  let pollInterval: ReturnType<typeof setInterval> | null = null;

  watch(container, (el) => {
    if (el) {
      // Container just became available in the DOM — render the CAPTCHA widget.
      if (window.AwsWafCaptcha) {
        renderCaptcha();
        return;
      }
      // jsapi.js is loaded with defer; poll until AwsWafCaptcha is available.
      const interval = setInterval(() => {
        if (window.AwsWafCaptcha) {
          clearInterval(interval);
          pollInterval = null;
          renderCaptcha();
        }
      }, 100);
      pollInterval = interval;
    } else {
      // Container removed from DOM — clean up poll interval and reset loading state.
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      loading.value = true;
    }
  });

  return {
    solved: readonly(solved),
    failed: readonly(failed),
    loading: readonly(loading),
  };
}
